import submit from "submit-form";
import { getExerciseName } from "../parts/modals/learn-more/blockHelpers";
import addMetadata from "./add-metadata";

export const METCON_TYPES = [
  { value: "amrap", label: "AMRAP" },
  { value: "emom", label: "EMOM" },
  { value: "forTime", label: "For Time" },
  { value: "tabata", label: "Tabata" },
  { value: "chipper", label: "Chipper" },
  { value: "ladder", label: "Ladder" },
  { value: "custom", label: "Custom" },
];

export const PARAMETER_OPTIONS = [
  "reps",
  "load",
  "time",
  "distance",
  "rest",
  "minutes",
  "freeform",
];

export const RESULT_MEASUREMENTS = [
  { value: "time", label: "Time" },
  { value: "rounds", label: "Rounds" },
  { value: "other", label: "Other" },
];

export function getEmptyExercise() {
  return {
    exercise: "",
    primary: "",
    type: "",
    parameters: [],
    reps: "",
    load: "",
    time: "",
    distance: "",
    rest: "",
    minutes: "",
    freeform: "",
    instructionVideo: null,
    instructions: null,
    primaryMuscleGroup: null,
    secondaryMuscleGroup: null,
    exerciseTypeField: null,
  };
}

/**
 * Parse load string in format "90/65" or "90" into separate male/female values
 * @param {string} loadString - Load string like "90/65" or "90"
 * @returns {object} - { male: number, female: number|null, isSingle: boolean }
 */
export function parseLoadString(loadString) {
  if (!loadString || typeof loadString !== "string") {
    return { male: null, female: null, isSingle: true };
  }

  const trimmed = loadString.trim();
  if (!trimmed) {
    return { male: null, female: null, isSingle: true };
  }

  // Check if it contains a forward slash
  if (trimmed.includes("/")) {
    const parts = trimmed.split("/").map((part) => part.trim());
    if (parts.length === 2) {
      const maleValue = parseFloat(parts[0]);
      const femaleValue = parseFloat(parts[1]);

      if (!isNaN(maleValue) && !isNaN(femaleValue)) {
        return {
          male: maleValue,
          female: femaleValue,
          isSingle: false,
        };
      }
    }
  }

  // Single value
  const singleValue = parseFloat(trimmed);
  if (!isNaN(singleValue)) {
    return {
      male: singleValue,
      female: null,
      isSingle: true,
    };
  }

  return { male: null, female: null, isSingle: true };
}

/**
 * Format load values into display string
 * @param {number} male - Male load value
 * @param {number} female - Female load value (optional)
 * @returns {string} - Formatted string like "90/65" or "90"
 */
export function formatLoadString(male, female) {
  if (male && female) {
    return `${male}/${female}`;
  } else if (male) {
    return male.toString();
  }
  return "";
}

export function buildExerciseOptions(exerciseLibrary) {
  const options = [];

  if (exerciseLibrary) {
    Object.keys(exerciseLibrary).forEach((category) => {
      // Add primary movements (except assistant and other) as options
      if (category !== "assistant" && category !== "other") {
        const exerciseObj = {
          primary: category,
          secondary: null,
          type: "primary",
        };

        const formattedLabel = getExerciseName(exerciseObj);

        options.push({
          value: category,
          label: formattedLabel,
          primary: category,
          type: "primary",
          category,
        });
      }

      // Add secondary movements from the exercise library
      if (Array.isArray(exerciseLibrary[category])) {
        exerciseLibrary[category].forEach((exercise) => {
          if (
            exercise &&
            exercise.movement &&
            typeof exercise.movement === "string"
          ) {
            const type = category === "assistant" ? "assistant" : "secondary";
            const exerciseObj = {
              primary: category,
              secondary: exercise.movement,
              type: type,
            };

            const formattedLabel = getExerciseName(exerciseObj);

            options.push({
              value: exercise.movement,
              label: formattedLabel,
              primary: category,
              type: type,
              category,
            });
          }
        });
      }
    });
  }

  return options.sort((a, b) => {
    const labelA = a.label || "";
    const labelB = b.label || "";
    return labelA.localeCompare(labelB);
  });
}

export function validateCurrentExercise(currentExercise, locals) {
  const { flash } = locals;

  if (!currentExercise.exercise.trim()) {
    flash("error", "Exercise name is required");
    submit("/actions/refresh");
    return false;
  }
  if (currentExercise.parameters.length === 0) {
    flash("error", "At least one parameter must be selected");
    submit("/actions/refresh");
    return false;
  }

  for (const param of currentExercise.parameters) {
    if (
      !currentExercise[param] ||
      currentExercise[param].toString().trim() === ""
    ) {
      flash("error", `${param.replace("_", " ")} value is required`);
      submit("/actions/refresh");
      return false;
    }
  }
  return true;
}

export function validateMetcon(metconData, exercisesList, locals) {
  const { flash } = locals;

  if (!metconData.subType) {
    flash("error", "Metcon type is required");
    submit("/actions/refresh");
    return false;
  }
  if (exercisesList.length === 0) {
    flash("error", "At least one exercise is required");
    submit("/actions/refresh");
    return false;
  }
  if (["amrap", "emom"].includes(metconData.subType) && !metconData.duration) {
    flash(
      "error",
      `Duration is required for ${metconData.subType.toUpperCase()}`
    );
    submit("/actions/refresh");
    return false;
  }
  return true;
}

/**
 * Populates exercise metadata from exerciseLibrary
 */
export function populateExerciseMetadata(
  exercise,
  exerciseLibrary,
  primaryExercises = {}
) {
  if (!exercise.exercise || !exercise.primary) {
    return exercise;
  }

  // Use addMetadata utility function to populate metadata
  // For primary exercises, use primaryExercises; for secondary/assistant, use exerciseLibrary
  const updatedExercise = addMetadata(
    { ...exercise }, // clone exercise
    exercise.type === "primary" ? exercise.primary : exercise.exercise, // movement name
    exercise.primary, // newPrimary
    primaryExercises,
    exerciseLibrary
  );

  // Map the metadata to our metcon-specific fields
  return {
    ...exercise,
    instructionVideo:
      updatedExercise.video !== "null" ? updatedExercise.video : null,
    instructions:
      updatedExercise.instructions !== "null"
        ? updatedExercise.instructions
        : null,
    primaryMuscleGroup:
      updatedExercise.primaryMuscleGroup !== "null"
        ? updatedExercise.primaryMuscleGroup
        : null,
    secondaryMuscleGroup:
      updatedExercise.secondaryMuscleGroup !== "null"
        ? updatedExercise.secondaryMuscleGroup
        : null,
    exerciseTypeField:
      updatedExercise.exerciseTypeField !== "null"
        ? updatedExercise.exerciseTypeField
        : null,
  };
}

export function createExerciseToAdd(currentExercise) {
  const exerciseToAdd = {
    exercise: currentExercise.exercise,
    primary: currentExercise.primary,
    type: currentExercise.type,
    parameters: [...currentExercise.parameters],
    // Include metadata fields
    instructionVideo: currentExercise.instructionVideo,
    instructions: currentExercise.instructions,
    primaryMuscleGroup: currentExercise.primaryMuscleGroup,
    secondaryMuscleGroup: currentExercise.secondaryMuscleGroup,
    exerciseTypeField: currentExercise.exerciseTypeField,
  };

  currentExercise.parameters.forEach((param) => {
    if (param === "load" && currentExercise[param]) {
      const parsed = parseLoadString(currentExercise[param].toString());
      if (parsed.male !== null) {
        exerciseToAdd.load = parsed.male;
        if (parsed.female !== null) {
          exerciseToAdd.loadFemale = parsed.female;
        }
      }
    } else if (currentExercise[param]) {
      exerciseToAdd[param] = currentExercise[param];
    }
  });

  return exerciseToAdd;
}

export function addOrUpdateExercise(
  exercisesList,
  exerciseToAdd,
  editingIndex
) {
  if (editingIndex !== null) {
    const updatedList = [...exercisesList];
    updatedList[editingIndex] = exerciseToAdd;
    return { updatedList, newEditingIndex: null };
  } else {
    return {
      updatedList: [...exercisesList, exerciseToAdd],
      newEditingIndex: null,
    };
  }
}

/**
 * Ensures a value has a unit suffix if it's just a number
 * @param {string|number} value - The value to check
 * @param {string} defaultUnit - Default unit to append if missing
 * @returns {string} Value with unit
 */
function ensureUnit(value, defaultUnit) {
  const str = String(value).trim();
  // Check if the value already has a unit suffix (letters at the end)
  const hasUnit = /[a-zA-Z]/.test(str);
  return hasUnit ? str : `${str}${defaultUnit}`;
}

export function formatExerciseDisplay(
  exercise,
  locals,
  alreadyConverted = false
) {
  const parts = [];
  const { fromMetric, withWeight } = locals;

  if (!exercise.parameters) return "";

  // Define strict parameter order
  const PARAMETER_ORDER = [
    "reps",
    "load",
    "time",
    "distance",
    "minutes",
    "freeform",
    "rest",
  ];

  // Filter to only parameters that exist and are enabled
  const orderedParams = PARAMETER_ORDER.filter(
    (p) => exercise.parameters.includes(p) && exercise[p]
  );

  orderedParams.forEach((param) => {
    switch (param) {
      case "reps":
        parts.push(`${exercise[param]} Reps`);
        break;
      case "load":
        if (exercise.loadFemale && exercise[param]) {
          // Convert from kg to user's preferred units if not already converted
          const maleValue = alreadyConverted
            ? exercise[param]
            : fromMetric(exercise[param]);
          const femaleValue = alreadyConverted
            ? exercise.loadFemale
            : fromMetric(exercise.loadFemale);
          const maleDisplay = withWeight(maleValue);
          const femaleDisplay = withWeight(femaleValue);
          const maleMatch = maleDisplay.match(/^([\d.]+)\s*(.*)$/);
          const femaleMatch = femaleDisplay.match(/^([\d.]+)\s*(.*)$/);
          if (maleMatch && femaleMatch) {
            parts.push(`@ ${maleMatch[1]}/${femaleMatch[1]}${maleMatch[2]}`);
          } else {
            parts.push(`@ ${maleDisplay}/${femaleDisplay}`);
          }
        } else if (exercise[param]) {
          // Convert from kg to user's preferred units if not already converted
          const loadValue = alreadyConverted
            ? exercise[param]
            : fromMetric(exercise[param]);
          parts.push(`@ ${withWeight(loadValue)}`);
        }
        break;
      case "time":
        parts.push(`@ ${ensureUnit(exercise[param], "s")}`);
        break;
      case "distance":
        parts.push(`@ ${ensureUnit(exercise[param], "m")}`);
        break;
      case "minutes":
        parts.push(`@ ${ensureUnit(exercise[param], "min")}`);
        break;
      case "freeform":
        parts.push(exercise[param]);
        break;
      case "rest":
        parts.push(`- ${ensureUnit(exercise[param], "s")} Rest`);
        break;
    }
  });

  return parts.join(" ");
}

/**
 * Formats a metcon title for display
 */
export function formatMetconTitle(exercise) {
  if (!exercise || exercise.primary !== "metcon") return "";

  const typeLabel = (exercise.subType || "").toUpperCase();
  let titleParts = [typeLabel];

  if (exercise.duration && exercise.duration > 0) {
    const minutes = Math.floor(exercise.duration / 60);
    const seconds = exercise.duration % 60;
    if (minutes > 0 && seconds > 0) {
      titleParts.push(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    } else if (minutes > 0) {
      titleParts.push(`${minutes} min`);
    } else {
      titleParts.push(`${seconds}s`);
    }
  }

  if (exercise.totalRounds) {
    titleParts.push(`${exercise.totalRounds} Rounds`);
  }

  return titleParts.filter(Boolean).join(" - ");
}

/**
 * Formats metcon exercises list for display
 */
export function formatMetconExercisesList(exercise) {
  if (!exercise?.sets?.length) return "";

  return exercise.sets
    .map((set) => {
      const params = [];

      if (set.reps) params.push(set.reps);
      if (set.load) {
        if (set.loadFemale) {
          params.push(`${set.load}/${set.loadFemale}kg`);
        } else {
          params.push(`${set.load}kg`);
        }
      }
      if (set.time) params.push(`${set.time}s`);
      if (set.distance) params.push(`${set.distance}m`);
      if (set.rest_time) params.push(`${set.rest_time}s rest`);
      if (set.minutes) params.push(`${set.minutes} min`);
      if (set.free_form) params.push(set.free_form);

      const paramStr = params.length > 0 ? params.join(" ") + " " : "";
      return `${paramStr}${set.exercise}`;
    })
    .join(" • ");
}

/**
 * Check if exercise has video metadata for display
 */
export function hasVideoMetadata(exercise) {
  if (exercise.primary === "metcon") {
    return exercise.sets?.some((set) => set.instructionVideo);
  }
  return !!exercise.video;
}

/**
 * Get video count for metcon exercises
 */
export function getMetconVideoCount(exercise) {
  if (exercise.primary !== "metcon") return 0;
  return exercise.sets?.filter((set) => set.instructionVideo).length || 0;
}

export function createFormSubmissionData(
  metconData,
  exercisesList,
  notes,
  locals,
  date,
  editExercise = null
) {
  const form = {
    primary: "metcon",
    type: "assistant",
    secondary: metconData.subType,
    ...metconData,
    notes,
    sets: exercisesList,
  };

  if (editExercise?.cycle) {
    form.cycle = editExercise.cycle;
  } else if (locals.params?.cycleId || locals.query?.cycle) {
    form.cycle = {
      id: locals.params?.cycleId || locals.query.cycle,
      isScheduled: false,
      program: locals.query.program,
      programWeek: locals.query.programWeek || locals.query.week,
      programSession:
        locals.query.programSession ||
        (parseInt(locals.query.session)
          ? locals.query.session
          : locals.query.session?.order),
    };
  }

  if (date) {
    delete form.cycle;
    form.date = date.clone().toDate();
    form.utcDate = parseInt(date.clone().format("YYYYMMDD"), 10);
  }

  if (form.subType !== "ladder") {
    delete form.ladderDirection;
    delete form.ladderIncrement;
  }

  if (form.customName === undefined) form.customName = null;

  return form;
}

/**
 * Format time in seconds to a human-readable string
 * @param {number} seconds - The number of seconds
 * @returns {string} Formatted time string (e.g., "10 minutes and 30 seconds")
 */
export const formatTimeExplicit = (seconds) => {
  const mins = Math.floor(Math.abs(seconds) / 60);
  const secs = Math.abs(seconds) % 60;
  const sign = seconds < 0 ? "-" : "";

  if (mins > 0 && secs > 0) {
    return `${sign}${mins} Minutes and ${secs} Seconds`;
  } else if (mins > 0) {
    return `${sign}${mins} Minutes`;
  } else {
    return `${sign}${secs} Seconds`;
  }
};

/**
 * Format metcon result for display
 * @param {Object} result - The metcon result object
 * @returns {string} Formatted result string
 */
export const formatMetconResult = (result) => {
  // Handle "other" field first (free-form text)
  if (result.other) {
    return result.other;
  }

  // Handle time-based results
  if (result.time !== undefined && result.time !== null) {
    const timeInSeconds =
      typeof result.time === "string" ? parseInt(result.time, 10) : result.time;
    return formatTimeExplicit(timeInSeconds);
  }

  // Handle rounds-based results
  if (result.rounds !== undefined && result.rounds !== null) {
    if (result.reps !== undefined && result.reps !== null && result.reps > 0) {
      return `${result.rounds} Rounds and ${result.reps} Reps`;
    }
    return `${result.rounds} Rounds`;
  }

  // Handle reps-based without rounds results
  if (result.reps !== undefined && result.reps !== null) {
    return `${result.reps} Reps`;
  }

  // Fallback
  return "";
};
